import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { artifactoryService } from '../services/artifactoryService';
import { createArtifact } from '../services/artifactService';
import { createWorkflow } from '../services/sessionService';
import { ArtifactType } from '../types/artifact';
import { SendDestination, SendMeta } from '../types/sendTo';
import { Message } from '../contexts/ConversationContext';

interface UseSendToReturn {
  summarize: (sessionId: string) => Promise<string>;
  isSummarizing: boolean;
  sendToDestination: (content: string, destination: SendDestination, meta: SendMeta) => Promise<void>;
  isSending: boolean;
  error: string | null;
}

export function useSendTo(): UseSendToReturn {
  const navigate = useNavigate();
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const summarize = useCallback(async (sessionId: string): Promise<string> => {
    setIsSummarizing(true);
    setError(null);
    try {
      const response = await api.post(`/chat/sessions/${sessionId}/summarize`);
      return response.data.summary;
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to generate summary';
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsSummarizing(false);
    }
  }, []);

  const sendToDestination = useCallback(async (
    content: string,
    destination: SendDestination,
    meta: SendMeta
  ) => {
    setIsSending(true);
    setError(null);
    try {
      const dateStr = new Date().toLocaleDateString();

      if (destination === 'artifactory_project') {
        await artifactoryService.scaffoldProject(
          meta.sessionId,
          content
        );
        // Navigate to projects list — the new manifest will appear there
        navigate('/artifactory');

      } else if (destination === 'artifact') {
        // Full-chat path → PERSONA_CHAT artifact with structured message
        // array, so the viewer re-renders each MessageBubble with its
        // phase bubble intact. `transcript_markdown` rides alongside as
        // a graceful fallback / export format.
        // Summarize path → plain MARKDOWN artifact (content is a string,
        // TextArtifactViewer handles ReactMarkdown rendering).
        const isFullChat = meta.sendMode === 'full' && Array.isArray(meta.messages) && meta.messages.length > 0;
        const artifactType = isFullChat ? ArtifactType.PERSONA_CHAT : ArtifactType.MARKDOWN;
        const artifactContent: any = isFullChat
          ? {
              messages: meta.messages,
              persona_name: meta.personaName,
              session_id: meta.sessionId,
              created_at: new Date().toISOString(),
              transcript_markdown: content,
            }
          : content;
        const artifact = await createArtifact({
          name: `Chat: ${meta.personaName} - ${dateStr}`,
          type: artifactType,
          content: artifactContent,
          session_id: undefined,
          metadata: {
            source: 'persona_chat',
            source_session_id: meta.sessionId,
            persona_id: meta.personaId,
            persona_name: meta.personaName,
            send_mode: meta.sendMode,
            message_count: meta.messageCount
          },
          tags: ['from-chat', meta.personaName.toLowerCase().replace(/\s+/g, '-')]
        });
        navigate(`/artifacts/${artifact.id}`);

      } else if (destination === 'workflow') {
        const blankWorkflow = {
          name: `From Chat: ${meta.personaName} - ${dateStr}`,
          description: content.substring(0, 200),
          phases: [],
          nodes: [],
          connections: []
        };
        const created = await createWorkflow(blankWorkflow);
        const workflowId = created.id;

        await api.post(`/workflows/${workflowId}/mutate`, {
          current_workflow: created,
          intent: content,
          available_personas: [],
          available_tools: [],
          user_preferences: { auto_apply: true, preserve_positions: true }
        });

        navigate(`/workflows/${workflowId}?chat=open`);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to send';
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsSending(false);
    }
  }, [navigate]);

  return { summarize, isSummarizing, sendToDestination, isSending, error };
}

/**
 * Format messages as a readable markdown transcript — includes the full
 * phase trail per assistant turn (tool calls, peer events, heartbeats)
 * using the server-built labels. Mirrors what the user saw live in the
 * phase bubble so the artifact is a faithful record, not just the text.
 */
export function formatTranscript(messages: Message[], personaName: string, sessionId: string): string {
  const dateStr = new Date().toLocaleDateString();
  const chatMessages = messages.filter(m => m.role === 'user' || m.role === 'assistant');

  const blocks = chatMessages.map(m => {
    const label = m.role === 'user' ? 'You' : personaName;
    const heading = `### ${label}`;
    const body = m.content || '_(empty)_';

    if (m.role !== 'assistant' || !m.metadata) {
      return `${heading}\n\n${body}`;
    }

    const parts: string[] = [heading, '', body];

    // Phase trail — nest via parent_tool_call_id so sub-events indent
    // under their parent tool. Mirrors the UI phase bubble exactly.
    const phaseEvents = Array.isArray(m.metadata.phase_events)
      ? (m.metadata.phase_events as any[])
      : [];
    if (phaseEvents.length > 0) {
      const turnMs =
        typeof m.metadata.duration_ms === 'number' ? m.metadata.duration_ms : null;
      const turnSummary = turnMs != null ? ` · ${(turnMs / 1000).toFixed(1)}s total` : '';
      parts.push('');
      parts.push(`<details><summary>Phase trail — ${phaseEvents.length} phases${turnSummary}</summary>`);
      parts.push('');
      for (const e of phaseEvents) {
        const indent = (e?.details?.parent_tool_call_id) ? '  - ' : '- ';
        const label = typeof e?.label === 'string' ? e.label : (e?.kind || '?');
        parts.push(`${indent}${label}`);
      }
      parts.push('');
      parts.push('</details>');
    }

    // Footer: model + cost + usage if present
    const meta = m.metadata;
    const footerBits: string[] = [];
    if (meta.model_used || meta.model) footerBits.push(String(meta.model_used || meta.model));
    if (typeof meta.duration_ms === 'number') footerBits.push(`${(meta.duration_ms / 1000).toFixed(1)}s`);
    const usage = meta.usage as Record<string, any> | undefined;
    if (usage && typeof usage.total_tokens === 'number') footerBits.push(`${usage.total_tokens} tok`);
    if (typeof meta.estimated_cost_usd === 'number') footerBits.push(`~$${meta.estimated_cost_usd.toFixed(4)}`);
    if (footerBits.length > 0) {
      parts.push('');
      parts.push(`_${footerBits.join(' · ')}_`);
    }

    return parts.join('\n');
  });

  return [
    `## Conversation with ${personaName}`,
    `**Session:** ${sessionId} | **Date:** ${dateStr} | **Messages:** ${chatMessages.length}`,
    '',
    '---',
    '',
    blocks.join('\n\n'),
  ].join('\n');
}
